import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { LeadCard } from "@/components/common/LeadCard";
import { ActivityItem } from "@/components/common/ActivityItem";
import { SelectField } from "@/components/common/Field";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatINRShort, monthLabel } from "@/lib/format";
import { ArrowLeft, Mail, Phone, Car, ShieldCheck } from "lucide-react";

function monthOptions(n = 6) {
  const arr = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const v = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    arr.push({ value: v, label: i === 0 ? `This month (${monthLabel(v)})` : monthLabel(v) });
  }
  return arr;
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const months = monthOptions();
  const [month, setMonth] = useState(months[0].value);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/users/${id}/performance`, { params: { month } })
      .then((res) => setData(res.data))
      .catch((err) => {
        toast.error(err?.response?.data?.detail || "Could not load");
        navigate("/team/employees", { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, month, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loader label="Loading performance…" />;
  if (!data) return null;

  const { employee: e, performance: p, leads, activities } = data;
  const stats = [
    { k: "Leads", v: p.leads_created }, { k: "Dealerships", v: p.dealerships_created },
    { k: "Follow-ups", v: p.followups }, { k: "Deals Done", v: p.deals_done },
    { k: "Completed", v: p.completed_deals }, { k: "Lost", v: p.lost_leads },
    { k: "Conversion", v: `${p.conversion}%` }, { k: "Business", v: formatINRShort(p.business_amount) },
  ];

  return (
    <div className="animate-fade-in-up space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">{e.full_name.slice(0, 1)}</span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold text-foreground sm:text-2xl">{e.full_name}</h1>
          <div className="flex flex-wrap items-center gap-x-3 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {e.email}</span>
            {e.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {e.phone}</span>}
          </div>
        </div>
      </div>

      <SelectField value={month} onChange={setMonth} options={months} />

      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        {stats.map((s) => (
          <div key={s.k} className="rounded-xl border border-border bg-card p-3.5 text-center shadow-card">
            <div className="tnum font-display text-xl font-extrabold text-foreground">{s.v}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{s.k}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="leads">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="leads" className="space-y-2.5 pt-4">
          {leads.length ? leads.map((l) => <LeadCard key={l.id} lead={l} />)
            : <EmptyState icon={Car} title="No leads this month" />}
        </TabsContent>
        <TabsContent value="activity" className="pt-4">
          {activities.length ? (
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
              {activities.map((a) => <ActivityItem key={a.id} activity={a} />)}
            </div>
          ) : <EmptyState icon={ShieldCheck} title="No activity yet" />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
